import { useEffect, useRef, useState } from 'react';
import { api, type RadarNowcast } from '../api';

/**
 * Bulk precipitation motion for the area around a point, for the case no NWS warning
 * covers: ordinary rain.
 *
 * The server caches this for five minutes and shares it between every viewer, so the
 * polling here is cheap — it mostly hits that cache. Anchored on a rounded bbox rather
 * than the exact point so panning or nudging the home location does not blow the cache.
 */
const REFRESH_MS = 120_000;
/** Half-width of the box we ask about, in degrees. Wide enough to see rain coming. */
const PAD_DEG = 0.65;

export interface AnchoredNowcast extends RadarNowcast {
  /** The box this result actually describes. */
  bbox: [number, number, number, number];
}

export function useNowcast(point: [number, number] | null): AnchoredNowcast | null {
  const [nowcast, setNowcast] = useState<AnchoredNowcast | null>(null);
  const keyRef = useRef<string | null>(null);

  // Round to a coarse grid so small movements reuse the same server-side cache entry.
  const bbox: [number, number, number, number] | null = point
    ? [
        Math.round((point[0] - PAD_DEG) * 4) / 4,
        Math.round((point[1] - PAD_DEG) * 4) / 4,
        Math.round((point[0] + PAD_DEG) * 4) / 4,
        Math.round((point[1] + PAD_DEG) * 4) / 4,
      ]
    : null;
  const key = bbox ? bbox.join(',') : null;

  useEffect(() => {
    if (!bbox || !key) {
      setNowcast(null);
      return;
    }
    let cancelled = false;
    keyRef.current = key;

    const load = async () => {
      try {
        const r = await api.radarNowcast(bbox);
        // Carry the box with the result. Without it a caller cannot tell that the data
        // it is holding describes somewhere else entirely — which is how a tap on one
        // storm got answered with a different storm ninety miles away.
        if (!cancelled && keyRef.current === key) setNowcast({ ...r, bbox });
      } catch {
        // A missing nowcast just means no rain estimate; the NWS path is unaffected.
        if (!cancelled && keyRef.current === key) setNowcast(null);
      }
    };

    void load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return nowcast;
}
